/**
 * Bãi thử giao diện cho bản kê chi tiết lương (`/_dev/salary-detail`).
 *
 * Bản kê thật chỉ mở được sau khi đăng nhập, nên không soi được bố cục ở các
 * bề rộng màn hình lúc đang dựng. Trang này dựng lại đúng thành phần đó với
 * số liệu mẫu lấy theo ảnh chụp màn hình của tháng 8/2026, để kiểm bố cục ở
 * 375px / 768px / 1280px mà không cần tài khoản.
 *
 * Chỉ chạy ở bản dựng phát triển — route cha `_dev` chặn ở PROD.
 */

import { createFileRoute } from "@tanstack/react-router";
import { SalaryDetailView } from "@/features/payroll/components/salary-detail/SalaryDetailView";
import type { PayrollRecord } from "@/features/payroll/lib";
import type { WorkLedgerRow } from "@/features/payroll/workLedgerApi";

export const Route = createFileRoute("/_dev/salary-detail")({
  component: SalaryDetailDemo,
});

const CUSTOMERS = [
  "Nguyễn Thị Hà", "Trần Văn Nam", "Lê Minh Châu", "Phạm Thu Trang",
  "Hoàng Anh Tuấn", "Đỗ Thị Mai", "Vũ Quốc Bảo", "Bùi Thanh Hằng",
  "Ngô Đức Thắng", "Đặng Kim Ngân", "Lý Hoài Nam", "Trịnh Thu Hà",
  "Cao Văn Đạt", "Dương Mỹ Linh", "Hồ Ngọc Quỳnh",
];

function orders(): NonNullable<PayrollRecord["orders"]> {
  return Array.from({ length: 49 }, (_, i) => {
    const cv = i % 7 === 0;
    return {
      orderId: `o${i}`,
      code: `AT-2608-${String(i + 1).padStart(3, "0")}`,
      shootDate: `2026-08-${String((i % 28) + 1).padStart(2, "0")}`,
      customer: CUSTOMERS[i % CUSTOMERS.length]!,
      product: cv ? "CV Chuyên nghiệp" : "Ảnh thẻ",
      detail: cv ? "CV Pro" : "Khách AT",
      amount: cv ? 450_000 : 250_000,
      pay: cv ? 50_000 : 30_000,
    };
  });
}

const RECORD: PayrollRecord = {
  staffId: "NV07",
  name: "Trần Thị Minh Anh",
  department: "photo_idphoto",
  employment: "fulltime",
  branch: "sg_32",
  // Số liệu mẫu được đặt cho TỰ KHỚP, đúng như máy tính lương thật ràng buộc:
  //   commission = tổng tiền dòng đơn + tổng tiền phát sinh + hoa hồng bán thêm
  //              = 1.610.000 + 115.000 + 200.000 = 1.925.000
  //   totalPay   = cứng + hoa hồng + mốc + động lực + cộng tự do
  //              = 3.000.000 + 1.925.000 + 500.000 + 400.000 + 200.000
  // Bãi thử mà số không khớp thì người đọc học sai cách đối chiếu.
  baseSalary: 3_000_000,
  commission: 1_925_000,
  milestoneBonus: 500_000,
  upsellMilestoneBonus: 150_000,
  upsellCommission: 200_000,
  motivationBonus: 400_000,
  streakBonus: 100_000,
  championBonus: 150_000,
  streakDays: 6,
  totalPay: 6_025_000,
  orders: orders(),
  surchargeItems: [
    { orderId: "o3", code: "AT-2608-004", shootDate: "2026-08-04", customer: "Phạm Thu Trang", kind: "anh_chinh", label: "Ảnh chỉnh", qty: 2, pay: 20_000 },
    { orderId: "o9", code: "AT-2608-010", shootDate: "2026-08-10", customer: "Đặng Kim Ngân", kind: "anh_chinh", label: "Ảnh chỉnh thêm", qty: 3, pay: 30_000 },
    { orderId: "o12", code: "AT-2608-013", shootDate: "2026-08-13", customer: "Cao Văn Đạt", kind: "ghep_phong", label: "Ghép phông", qty: 1, pay: 15_000 },
    { orderId: "o20", code: "AT-2608-021", shootDate: "2026-08-21", customer: "Lý Hoài Nam", kind: "thay_toc", label: "Thay tóc", qty: 1, pay: 10_000 },
    { orderId: "o27", code: "AT-2608-028", shootDate: "2026-08-25", customer: "Hồ Ngọc Quỳnh", kind: "combo_in_chinh", label: "Combo in + chỉnh", qty: 2, pay: 40_000 },
  ],
  upsellItems: [
    { orderId: "o5", customer: "Đỗ Thị Mai", label: "Nâng cấp khung ảnh", amount: 1_200_000, matured: true },
    { orderId: "o18", customer: "Dương Mỹ Linh", label: "In thêm 10 ảnh 10x15", amount: 800_000, matured: true },
    { orderId: "o31", customer: "Ngô Đức Thắng", label: "Album mini", amount: 600_000, matured: false },
  ],
  customAdditionItems: [{ Label: "Hỗ trợ xăng xe", Amount: 200_000 }],
  customAdditions: 200_000,
  adjustmentNote: "Bù ca trực chủ nhật 16/08 do bạn Ngân nghỉ ốm.",
};

/**
 * Hồ sơ thứ hai: một người kiêm CẢ Hậu kỳ lẫn CSKH, để soi luôn phần sổ từng
 * job. Số chốt cố ý LỆCH với sổ (480 ảnh trong sổ vs 500 ảnh đã chốt) — cảnh
 * báo lệch là thứ khó nhìn nhất trong bố cục, phải xem được ở bãi thử.
 */
const LEDGER_RECORD: PayrollRecord = {
  staffId: "NV12",
  name: "Phạm Quốc Hùng",
  department: "retouch",
  employment: "fulltime",
  branch: "sg_32",
  baseSalary: 5_000_000,
  commission: 4_340_000,
  totalPay: 9_340_000,
  retouchBreakdown: { photoCount: 500, photoPay: 4_000_000 },
  cskhBreakdown: {
    extraPhotoRevenue: 3_400_000,
    extraPhotoCommission: 340_000,
    printRevenue: 0,
    printCommission: 0,
  },
};

/**
 * Hồ sơ thứ ba: Trợ lý Studio, trả theo giờ ca đã duyệt (QĐ 0164).
 *
 * Số đặt cho TỰ KHỚP đúng như máy tính lương:
 *   hourlyPay = RoundVND(19,5 × 30.000) = 585.000
 *   totalPay  = cứng + tiền giờ + tiền tăng ca = 2.000.000 + 585.000 + 0
 *
 * 🔴 `hoursWorked: 20` cố ý LỆCH với `approvedHours: 19.5` — đó là số nguyên
 * máy chủ làm tròn để giữ trên bản ghi. Trên màn này nó phải KHÔNG hiện ở
 * đâu cả: mục "🏪 Lễ tân" vốn vẽ cho mọi phòng không phải thợ, nay đã chặn
 * phòng này. Thấy chữ "Lễ tân" hay thấy số 20 ở khối thứ ba là hỏng.
 */
const ASSISTANT_RECORD: PayrollRecord = {
  staffId: "TL03",
  name: "Nguyễn Thị Bích Ngọc",
  department: "studio_assistant",
  employment: "fulltime",
  branch: "sg_32",
  baseSalary: 2_000_000,
  commission: 0,
  hoursWorked: 20,
  overtimeHours: 0,
  overtimePay: 0,
  // Cố ý KHÔNG có ô `hourlyPay` ở tầng bản ghi, dù hợp đồng ĐÃ khai ô đó từ
  // 18/09/2026. Mục này phải lấy tiền giờ từ bảng kê bên dưới: bảng kê giữ
  // `approvedHours` giờ lẻ thật, còn ô ở tầng bản ghi đi cùng `hoursWorked`
  // đã làm tròn. Thiếu ô ở đây mà màn vẫn ra đúng số tức là nó đang đọc đúng
  // chỗ.
  totalPay: 2_585_000,
  studioAssistantBreakdown: {
    shiftCount: 13,
    approvedHours: 19.5,
    commonHourlyRate: 30_000,
    hourlyRateAdjustment: 5_000,
    hourlyRate: 35_000,
    hourlyPay: 585_000,
    overtimeHours: 0,
    overtimePay: 0,
    noApprovedShifts: false,
    rateNotPositive: false,
  },
};

/**
 * Hồ sơ thứ tư: Lễ tân — mục có dòng "Tiền công giờ thường" CEO chốt 1a
 * (18/09/2026).
 *
 * Số đặt cho TỰ KHỚP đúng như máy tính lương lễ tân:
 *   tổng = cứng + tiền giờ + hoa hồng bán thêm + tiền tăng ca
 *        = 4.000.000 + 5.432.000 + 890.000 + 75.000 = 10.397.000
 *   tăng ca = (214 − 208 giờ chuẩn tháng) × 25.000 × 0,5 = 75.000
 *   hoa hồng = 12×20.000 + 3×100.000 + 5×30.000 + 1×200.000 = 890.000
 *
 * 🔴 `hourlyPay` cố ý KHÔNG bằng `hoursWorked × 25.000` (5.350.000): người này
 * có 14,4 giờ trực lễ tết ăn đơn giá 30.000 của riêng ca đó, và số giờ trên
 * bản ghi là số ĐÃ LÀM TRÒN từ 214,4. Đây đúng là cảnh mà lời chú dưới dòng
 * tiền nói tới — bãi thử phải dựng được nó, không thì không ai soi được lời
 * chú ấy có thật hay chỉ là chữ cho đẹp.
 *
 * ⚠️ Năm dòng "Bán thêm — …" ở đây nhận SỐ LƯỢT (12, 3, 5, 1) chứ không phải
 * tiền, đúng như máy chủ gửi. Chính hồ sơ này làm lộ ra lỗi màn in chúng thành
 * "12đ / 3đ / 5đ"; CEO chốt `1a` cùng ngày nên đã vá — nay hiện "12 lượt" và
 * tiêu đề mục là "· 21 lượt bán thêm". Giữ nguyên số ở đây: đổi chúng thành
 * tiền là xoá mất cái cảnh đã làm lộ lỗi.
 */
const RECEPTION_RECORD: PayrollRecord = {
  staffId: "LT05",
  name: "Võ Thị Kim Chi",
  department: "reception",
  employment: "fulltime",
  branch: "sg_32",
  baseSalary: 4_000_000,
  commission: 890_000,
  hoursWorked: 214,
  hourlyPay: 5_432_000,
  overtimeHours: 6,
  overtimePay: 75_000,
  totalPay: 10_397_000,
  upsaleBreakdown: {
    atCv: 12,
    concept2: 3,
    clothing: 5,
    dn1buoi: 1,
    dn1ngay: 0,
    total: 21,
  },
};

/**
 * Hồ sơ thứ năm và sáu: tháng lương 10/2026, buổi chụp từ 01/10 tính theo công
 * (QĐ 0179). Phiếu chỉ ghi thạch lương thảo, không ghi số công (CEO duyệt bản
 * phác 22/09/2026).
 *
 * Số theo biểu giá mặc định — một công 300.000đ; thợ chụp: Basic 2/3 công, Cao
 * cấp 1 công, buổi doanh nghiệp studio 900.000đ, ngoại cảnh 1.200.000đ; thợ
 * trang điểm: Basic 1/2 công — và đặt cho TỰ KHỚP như máy lương: phần của mình
 * × hệ số bậc, làm tròn nghìn; thấp hơn mức cũ đã chia (cần ÷ đi) thì bù lên.
 *
 * Hồ sơ 5, thợ chụp G2 (×5/6), mức cũ bậc 1 (Basic 150.000 · Cao cấp 250.000):
 *   Basic, một thợ          200.000 × 5/6 = 167.000 — trên mức cũ, không bù
 *   Cao cấp, hai thợ        250.000 ÷ 2 = 125.000 — bằng mức cũ 250.000 ÷ 2
 *   DN studio, cần 3 đi 2   750.000 × 3 ÷ 2 = 1.125.000; mức cũ 900.000 × 3 ÷ 2 ⇒ bù 225.000
 *   DN ngoại cảnh, chưa khai số cần, đi 2   1.000.000; mức cũ 1.200.000 ⇒ bù 200.000
 *   concept 2 (tỷ lệ 0,5 của đơn Basic)     100.000 × 5/6 = 83.000; mức cũ 100.000 ⇒ bù 17.000
 *   DN ngoại cảnh có mức riêng +100.000/buổi, cần 3 đi 2 (câu 3a):
 *     (1.000.000 + 100.000) × 3 ÷ 2 = 1.650.000, trong đó mức riêng 150.000;
 *     mức cũ (1.200.000 + 100.000) × 3 ÷ 2 = 1.950.000 ⇒ bù 300.000
 *   DN studio giá riêng 1.000.000/buổi, cần 3 đi 2 (câu 2a): 1.500.000, không bậc
 *   tổng = 167.000 + 125.000 + 1.350.000 + 1.200.000 + 1.950.000 + 1.500.000
 *        + 100.000 = 6.392.000
 * Hồ sơ 6, thợ trang điểm G3, đơn có hai thợ trang điểm:
 *   Basic 150.000 ÷ 2 = 75.000; concept 2 tiền phẳng 100.000 ÷ 2 = 50.000
 */
const PF_G2 = { craftTier: "G2", craftAnchor: 300_000, craftCoefficient: 5 / 6 } as const;
const PF_ORDER_1 = { orderId: "p1", code: "PF-2610-004", shootDate: "2026-10-03", customer: "Nguyễn Thị Hà" } as const;

const PHOTO_PF_RECORD: PayrollRecord = {
  staffId: "PF04",
  name: "Lê Hoàng Phúc",
  department: "photo_profile",
  employment: "fulltime",
  branch: "sg_32",
  baseSalary: 0,
  commission: 6_392_000,
  totalPay: 6_392_000,
  orders: [
    { ...PF_ORDER_1, product: "Profile cơ bản", category: "profile", detail: "Profile Basic", amount: 1_500_000, ...PF_G2, craftUnits: 2 / 3, craftCrewSize: 1, pay: 167_000 },
    { orderId: "p2", code: "PF-2610-011", shootDate: "2026-10-05", customer: "Trần Văn Nam", product: "Profile cao cấp", category: "profile", detail: "Profile Cao cấp", amount: 3_200_000, ...PF_G2, craftUnits: 0.5, craftCrewSize: 2, pay: 125_000 },
    { orderId: "p3", code: "DN-2610-002", shootDate: "2026-10-09", customer: "Công ty An Phát", product: "Doanh nghiệp studio", category: "corporate", detail: "DN Studio", amount: 9_000_000, ...PF_G2, craftUnits: 4.5, craftJobUnits: 9, crewNeeded: 3, craftCrewSize: 2, craftFloorTopUp: 225_000, pay: 1_350_000 },
    { orderId: "p4", code: "DN-2610-005", shootDate: "2026-10-16", customer: "Công ty Minh Long", product: "Doanh nghiệp ngoại cảnh", category: "corporate", detail: "DN Ngoại cảnh", amount: 6_000_000, ...PF_G2, craftUnits: 4, craftJobUnits: 8, craftCrewSize: 2, craftFloorTopUp: 200_000, pay: 1_200_000 },
    { orderId: "p5", code: "DN-2610-008", shootDate: "2026-10-21", customer: "Công ty Hoà Bình", product: "Doanh nghiệp ngoại cảnh", category: "corporate", detail: "DN Ngoại cảnh", amount: 7_500_000, ...PF_G2, craftUnits: 6, craftJobUnits: 12, crewNeeded: 3, craftCrewSize: 2, craftStaffOverride: 150_000, craftFloorTopUp: 300_000, pay: 1_950_000 },
    { orderId: "p6", code: "DN-2610-012", shootDate: "2026-10-24", customer: "Công ty Sao Việt", product: "Doanh nghiệp studio", category: "corporate", detail: "DN Studio (giá riêng)", amount: 8_000_000, crewRate: 1_000_000, crewNeeded: 3, craftCrewSize: 2, pay: 1_500_000 },
  ],
  surchargeItems: [
    { ...PF_ORDER_1, kind: "concept_2", label: "Concept 2", ruleLabel: "PS concept 2 / trang phục / background", qty: 1, ...PF_G2, craftUnits: 1 / 3, craftCrewSize: 1, craftFloorTopUp: 17_000, pay: 100_000 },
  ],
};

const MAKEUP_PF_RECORD: PayrollRecord = {
  staffId: "MU09",
  name: "Phan Thị Thu Thảo",
  department: "makeup",
  employment: "fulltime",
  branch: "sg_32",
  baseSalary: 3_000_000,
  commission: 125_000,
  totalPay: 3_125_000,
  orders: [
    { orderId: "m1", code: "PF-2610-019", shootDate: "2026-10-12", customer: "Lê Minh Châu", product: "Profile cơ bản", category: "profile", detail: "Profile Basic", amount: 2_400_000, craftTier: "G3", craftAnchor: 300_000, craftCoefficient: 1, craftUnits: 0.25, craftCrewSize: 2, pay: 75_000 },
  ],
  surchargeItems: [
    { orderId: "m1", code: "PF-2610-019", shootDate: "2026-10-12", customer: "Lê Minh Châu", kind: "concept_2", label: "Concept 2", ruleLabel: "PS concept 2 / trang phục / background", qty: 1, craftCrewSize: 2, craftFlatShare: true, pay: 50_000 },
  ],
};

function ledgerRows(): WorkLedgerRow[] {
  const retouch = Array.from({ length: 24 }, (_, i): WorkLedgerRow => ({
    id: `r${i}`,
    dept: "retouch",
    kind: "photo",
    orderId: `lo${i}`,
    staffId: "NV12",
    staffName: "Phạm Quốc Hùng",
    month: "2026-08",
    qty: 20,
    amount: 0,
    source: i === 3 ? ("manual" as const) : ("auto" as const),
    note: i === 3 ? "Leader đếm lại: khách trả về 4 ảnh" : "Số ảnh khách đã chọn",
    happenedAt: "2026-08-10T10:00:00+07:00",
    orderCode: `PF-2608-${String(i + 1).padStart(3, "0")}`,
    customer: CUSTOMERS[i % CUSTOMERS.length]!,
    shootDate: `2026-08-${String((i % 28) + 1).padStart(2, "0")}`,
    createdAt: "2026-08-10T10:00:00+07:00",
    updatedAt: "2026-08-10T10:00:00+07:00",
  }));
  const cskh = Array.from({ length: 5 }, (_, i): WorkLedgerRow => ({
    id: `c${i}`,
    dept: "cskh",
    kind: i === 4 ? ("print" as const) : ("extra_photo" as const),
    orderId: `co${i}`,
    staffId: "NV12",
    staffName: "Phạm Quốc Hùng",
    month: "2026-08",
    qty: i === 4 ? 6 : 3,
    amount: i === 4 ? 1_200_000 : 550_000,
    source: "auto",
    note: i === 4 ? "Đơn in đã giao (chưa VAT, không gồm ship)" : "Phụ thu ảnh chỉnh sửa (chưa VAT)",
    happenedAt: "2026-08-15T14:00:00+07:00",
    orderCode: `PF-2608-1${String(i + 1).padStart(2, "0")}`,
    customer: CUSTOMERS[(i + 7) % CUSTOMERS.length]!,
    shootDate: `2026-08-1${i}`,
    createdAt: "2026-08-15T14:00:00+07:00",
    updatedAt: "2026-08-15T14:00:00+07:00",
  }));
  return [...retouch, ...cskh];
}

function SalaryDetailDemo() {
  return (
    <div className="mx-auto max-w-3xl space-y-8 p-3 md:p-6" data-testid="salary-detail-demo">
      <SalaryDetailView
        record={RECORD}
        month="2026-08"
        computedAt="2026-08-20T21:45:00+07:00"
        finalized={false}
      />
      <div data-testid="salary-detail-demo-ledger">
        <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">
          Hồ sơ 2 — kiêm Hậu kỳ + CSKH, có sổ từng job
        </p>
        <SalaryDetailView
          record={LEDGER_RECORD}
          roles={["retouch", "cskh"]}
          month="2026-08"
          computedAt="2026-08-20T21:45:00+07:00"
          finalized={false}
          ledger={ledgerRows()}
        />
      </div>
      <div data-testid="salary-detail-demo-assistant">
        <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">
          Hồ sơ 3 — Trợ lý Studio, trả theo giờ ca đã duyệt
        </p>
        <SalaryDetailView
          record={ASSISTANT_RECORD}
          month="2026-08"
          computedAt="2026-08-20T21:45:00+07:00"
          finalized={false}
        />
      </div>
      <div data-testid="salary-detail-demo-reception">
        <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">
          Hồ sơ 4 — Lễ tân, có dòng tiền công giờ thường
        </p>
        <SalaryDetailView
          record={RECEPTION_RECORD}
          month="2026-08"
          computedAt="2026-08-20T21:45:00+07:00"
          finalized={false}
        />
      </div>
      <div data-testid="salary-detail-demo-photo-pf">
        <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">
          Hồ sơ 5 — Thợ chụp Profile tháng 10/2026, tính theo công
        </p>
        <SalaryDetailView
          record={PHOTO_PF_RECORD}
          month="2026-10"
          computedAt="2026-10-31T21:45:00+07:00"
          finalized={false}
        />
      </div>
      <div data-testid="salary-detail-demo-makeup-pf">
        <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">
          Hồ sơ 6 — Trang điểm Profile tháng 10/2026, phát sinh chia phẳng
        </p>
        <SalaryDetailView
          record={MAKEUP_PF_RECORD}
          month="2026-10"
          computedAt="2026-10-31T21:45:00+07:00"
          finalized={false}
        />
      </div>
    </div>
  );
}
